import React from "react"
import type { CrudFilterState, FilterValue, QuickFilterDef } from "../../types/crudFilters.ts"
import { filterValueToString, isFieldFilterActive } from "../../utils/crudFilterParams.ts"

type CrudQuickFilterProps = {
  def: QuickFilterDef
  filterState: CrudFilterState
  onChange: (field: string, value: FilterValue) => void
  fullWidth?: boolean
}

const activeSelectClass =
  "border-electric-blue/50 bg-electric-blue/[0.06] text-deep-ocean ring-1 ring-electric-blue/20 font-medium"
const idleSelectClass = "border-slate-gray/30 bg-white text-slate-gray"

export function CrudQuickFilter({ def, filterState, onChange, fullWidth = false }: CrudQuickFilterProps) {
  const current = filterValueToString(filterState[def.field] ?? "")
  const isActive = isFieldFilterActive(filterState[def.field])
  const widthClass = fullWidth ? "w-full" : "shrink-0 w-[8.5rem]"

  if (def.type === "text") {
    return (
      <div className={widthClass}>
        <label className={fullWidth ? "mb-1 block text-xs font-medium text-slate-gray" : "sr-only"}>
          {def.label}
        </label>
        <input
          type="text"
          value={current}
          placeholder={def.placeholder ?? def.label}
          onChange={(e) => onChange(def.field, e.target.value || null)}
          className={`w-full rounded-lg border text-base md:text-sm focus:border-transparent focus:ring-2 focus:ring-electric-blue ${
            fullWidth ? "h-10 px-3" : "h-9 px-2.5"
          } ${isActive ? activeSelectClass : idleSelectClass}`}
        />
      </div>
    )
  }

  return (
    <div className={`relative ${widthClass}`}>
      <label className={fullWidth ? "mb-1 block text-xs font-medium text-slate-gray" : "sr-only"}>
        {def.label}
      </label>
      <select
        value={current}
        onChange={(e) => {
          const val = e.target.value
          if (def.field === "is_active" || def.field === "is_read") {
            onChange(def.field, val === "" ? null : val === "true")
          } else {
            onChange(def.field, val || null)
          }
        }}
        className={`w-full appearance-none truncate rounded-lg border focus:border-transparent focus:ring-2 focus:ring-electric-blue ${
          fullWidth ? "h-10 pl-3 pr-8 text-base md:text-sm" : "h-9 pl-2.5 pr-7 text-sm"
        } ${isActive ? activeSelectClass : idleSelectClass}`}
        title={isActive ? `${def.label}: ${current}` : def.label}
      >
        {def.options.map((opt) => (
          <option key={opt.value || "__all__"} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      <div className="absolute inset-y-0 right-0 flex items-center pr-1.5 pointer-events-none">
        {isActive ? (
          <span className="h-1.5 w-1.5 rounded-full bg-electric-blue mr-0.5" aria-hidden />
        ) : null}
        <svg className="h-3.5 w-3.5 text-slate-gray/70" viewBox="0 0 20 20" fill="currentColor">
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </div>
    </div>
  )
}
